import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1503428593586-e225b39bddfe?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #004744;
  font-style: italic;
`;

const TransportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TransportCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const TransportTitle = styled.h3`
  font-size: 1.8rem;
  color: #004744;
  margin-bottom: 1rem;
  font-style: italic;
  text-align: center;
`;

const TransportDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 1.5rem;
  flex: 1;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Transportation: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  // Transport options data
  const transportOptions = [
    {
      title: language === 'it' ? 'NAVETTE' : 'SHUTTLES',
      description: language === 'it' 
        ? 'Scegli comfort e comodità. Scegli la tua soluzione di trasporto navetta ideale. Pianifica oggi il tuo prossimo viaggio e lascia che la tua avventura cominci con il piede giusto.'
        : 'Choose comfort and convenience. Choose your ideal shuttle transport solution. Plan your next trip today and let your adventure get started on the right foot.',
      path: '/services/shuttles'
    },
    {
      title: language === 'it' ? 'AUTOBUS' : 'BUSES',
      description: language === 'it' 
        ? 'Scegli comfort e comodità. Scegli la tua soluzione di trasporto autobus ideale. Pianifica oggi il tuo prossimo viaggio e lascia che la tua avventura cominci con il piede giusto.'
        : 'Choose comfort and convenience. Choose your ideal bus transport solution. Plan your next trip today and let your adventure get started on the right foot.',
      path: '/services/buses'
    },
    {
      title: language === 'it' ? 'VOLI' : 'FLIGHTS',
      description: language === 'it' 
        ? 'Scegli comfort e comodità. Scegli la tua soluzione di trasporto aereo ideale. Pianifica oggi il tuo prossimo viaggio e lascia che la tua avventura cominci con il piede giusto.'
        : 'Choose comfort and convenience. Choose your ideal flight transport solution. Plan your next trip today and let your adventure get started on the right foot.',
      path: '/services/flights'
    },
    {
      title: language === 'it' ? 'TRENI' : 'TRAINS',
      description: language === 'it' 
        ? 'Scegli comfort e comodità. Scegli la tua soluzione di trasporto ferroviario ideale. Pianifica oggi il tuo prossimo viaggio e lascia che la tua avventura cominci con il piede giusto.'
        : 'Choose comfort and convenience. Choose your ideal train transport solution. Plan your next trip today and let your adventure get started on the right foot.',
      path: '/services/trains'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Trasporti | J-O-H' : 'Transportation | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri le migliori opzioni di trasporto per i tuoi spostamenti: navette, autobus, voli e treni' 
            : 'Discover the best transportation options for your travels: shuttles, buses, flights and trains'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-tenant">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'TRASPORTI' : 'TRANSPORTATION'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Confronta diverse opzioni di trasporto per trovare quella più adatta alle tue esigenze. Navette, autobus, voli o treni: scegli il modo migliore per spostarti e goditi il viaggio.'
              : 'Compare different transportation options to find the one that best suits your needs. Shuttles, buses, flights or trains: choose the best way to travel and enjoy the journey.'}
          </Description>
          
          <SectionTitle>{language === 'it' ? 'CONFRONTA TRASPORTI' : 'COMPARE TRANSPORTATION'}</SectionTitle>
          
          <TransportGrid>
            {transportOptions.map((option, index) => (
              <TransportCard key={index}>
                <TransportTitle>{option.title}</TransportTitle>
                <TransportDescription>{option.description}</TransportDescription>
                <ButtonContainer>
                  <Button variant="outline" to={option.path}>
                    {language === 'it' ? 'Scopri di più' : 'Find out more'}
                  </Button>
                </ButtonContainer>
              </TransportCard>
            ))}
          </TransportGrid>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Transportation; 